// src/pages/Dashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Spinner, Alert, Form, Table } from 'react-bootstrap';
import { FaMoneyBillWave, FaSync, FaExclamationTriangle, FaCheckCircle, FaClock } from 'react-icons/fa';
import api from '../service/api';

const NOMES_MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const mesAnterior = () => {
  const hoje = new Date();
  const m = hoje.getMonth(); // 0-based
  return {
    mes: String(m === 0 ? 12 : m).padStart(2, '0'),
    ano: (m === 0 ? hoje.getFullYear() - 1 : hoje.getFullYear()).toString(),
  };
};

const ultimos12Meses = (): { ano: string; mes: string }[] => {
  const resultado = [];
  const hoje = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    resultado.push({
      ano: d.getFullYear().toString(),
      mes: String(d.getMonth() + 1).padStart(2, '0'),
    });
  }
  return resultado;
};

interface PeriodoStats {
  ano: string;
  mes: string;
  qtdTotal: number;
  valTotal: number;
  qtdPago: number;
  valPago: number;
  qtdAberto: number;
  valAberto: number;
}

const isPago   = (m: any) => m.MOV_SITUACAO === 'P' || !!m.MOV_DATAPGTO;
const isAberto = (m: any) => !isPago(m) && m.MOV_SITUACAO !== 'C';

const calcPeriodo = (movs: any[], ano: string, mes: string): PeriodoStats => {
  const p = movs.filter((m: any) => m.MOV_ANO === ano && m.MOV_MES === mes);
  const pagos   = p.filter(isPago);
  const abertos = p.filter(isAberto);
  return {
    ano, mes,
    qtdTotal:  p.length,
    valTotal:  p.reduce((acc: number, m: any) => acc + Number(m.MOV_VALOR || 0), 0),
    qtdPago:   pagos.length,
    valPago:   pagos.reduce((acc: number, m: any) => acc + Number(m.MOV_TOTALPAGO || m.MOV_VALOR || 0), 0),
    qtdAberto: abertos.length,
    valAberto: abertos.reduce((acc: number, m: any) => acc + Number(m.MOV_VALOR || 0), 0),
  };
};

const Dashboard: React.FC = () => {
  const def = mesAnterior();
  const [filterAno, setFilterAno] = useState(def.ano);
  const [filterMes, setFilterMes] = useState(def.mes);

  const [periodo, setPeriodo]       = useState<PeriodoStats | null>(null);
  const [historico, setHistorico]   = useState<PeriodoStats[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const anoAtual = new Date().getFullYear();
  const anos  = Array.from({ length: 5 }, (_, i) => (anoAtual - i).toString());
  const meses = ['01','02','03','04','05','06','07','08','09','10','11','12'];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: movs } = await api.get('/movimentos');

      setPeriodo(calcPeriodo(movs, filterAno, filterMes));
      setHistorico(ultimos12Meses().map(({ ano, mes }) => calcPeriodo(movs, ano, mes)));
    } catch {
      setError('Não foi possível carregar os dados do dashboard.');
    } finally {
      setLoading(false);
    }
  }, [filterAno, filterMes]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const nomePeriodo = `${NOMES_MESES[parseInt(filterMes, 10) - 1]} / ${filterAno}`;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Dashboard</h1>
        <div className="d-flex gap-2 align-items-center">
          <Form.Select size="sm" style={{ width: 'auto' }} value={filterMes} onChange={e => setFilterMes(e.target.value)}>
            {meses.map(m => <option key={m} value={m}>{NOMES_MESES[parseInt(m, 10) - 1]} ({m})</option>)}
          </Form.Select>
          <Form.Select size="sm" style={{ width: 'auto' }} value={filterAno} onChange={e => setFilterAno(e.target.value)}>
            {anos.map(a => <option key={a} value={a}>{a}</option>)}
          </Form.Select>
          <Button variant="outline-primary" size="sm" onClick={fetchData} disabled={loading}>
            {FaSync({ className: loading ? 'me-2 spin' : 'me-2' })} Atualizar
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{FaExclamationTriangle({ className: 'me-2' })}{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando...</p>
        </div>
      ) : (
        <>
          {/* ── Cards do período selecionado ── */}
          <h5 className="text-muted mb-3">
            Movimentos — <strong>{nomePeriodo}</strong>
          </h5>
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="shadow-sm h-100 border-start border-secondary border-4">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center text-secondary">
                    {FaMoneyBillWave({ className: 'me-2' })} Total do período
                  </Card.Title>
                  <h2 className="text-secondary">{periodo?.qtdTotal ?? 0}</h2>
                  <div className="text-secondary fw-semibold">{formatCurrency(periodo?.valTotal ?? 0)}</div>
                  <small className="text-muted">movimentos lançados</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100 border-start border-success border-4">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center text-success">
                    {FaCheckCircle({ className: 'me-2' })} Pagos
                  </Card.Title>
                  <h2 className="text-success">{periodo?.qtdPago ?? 0}</h2>
                  <div className="text-success fw-semibold">{formatCurrency(periodo?.valPago ?? 0)}</div>
                  <small className="text-muted">
                    {(periodo?.qtdTotal ?? 0) > 0
                      ? `${Math.round(((periodo?.qtdPago ?? 0) / (periodo?.qtdTotal ?? 1)) * 100)}% do total`
                      : 'sem movimentos'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100 border-start border-warning border-4">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center text-warning">
                    {FaClock({ className: 'me-2' })} Em Aberto
                  </Card.Title>
                  <h2 className="text-warning">{periodo?.qtdAberto ?? 0}</h2>
                  <div className="text-warning fw-semibold">{formatCurrency(periodo?.valAberto ?? 0)}</div>
                  <small className="text-muted">
                    {(periodo?.qtdTotal ?? 0) > 0
                      ? `${Math.round(((periodo?.qtdAberto ?? 0) / (periodo?.qtdTotal ?? 1)) * 100)}% do total`
                      : 'sem movimentos'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Histórico últimos 12 meses ── */}
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold">Histórico — últimos 12 meses</Card.Header>
            <Card.Body className="p-0">
              <Table hover size="sm" className="mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Mês / Ano</th>
                    <th className="text-center">Qtd. Total</th>
                    <th className="text-end">Valor Total</th>
                    <th className="text-center">Qtd. Pagos</th>
                    <th className="text-end">Valor Pago</th>
                    <th className="text-center">Qtd. Aberto</th>
                    <th className="text-end">Valor Aberto</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(h => (
                    <tr key={`${h.ano}-${h.mes}`}>
                      <td><strong>{NOMES_MESES[parseInt(h.mes, 10) - 1]}</strong> / {h.ano}</td>
                      <td className="text-center">{h.qtdTotal}</td>
                      <td className="text-end">{formatCurrency(h.valTotal)}</td>
                      <td className="text-center text-success">{h.qtdPago}</td>
                      <td className="text-end text-success">{formatCurrency(h.valPago)}</td>
                      <td className="text-center text-warning">{h.qtdAberto}</td>
                      <td className="text-end text-warning">{formatCurrency(h.valAberto)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </>
  );
};

export default Dashboard;
